/**
 * Minimal toolbar for the hexcrawl editor
 * Provides a hand-drawn aesthetic UI for triggering procedural expansion
 */

export class Toolbar {
  private container: HTMLElement;
  private toolbar: HTMLElement;
  private buttons: Map<string, HTMLButtonElement> = new Map();

  constructor(container: HTMLElement) {
    this.container = container;
    this.toolbar = this.createToolbar();
    this.container.appendChild(this.toolbar);
  }

  private createToolbar(): HTMLElement {
    const toolbar = document.createElement("div");
    toolbar.className = "hexcrawl-toolbar";

    // Inline styles for hand-drawn aesthetic
    Object.assign(toolbar.style, {
      position: "absolute",
      top: "0",
      left: "0",
      right: "0",
      height: "60px",
      backgroundColor: "#fffef0", // Cream/parchment
      borderBottom: "2px solid #333",
      display: "flex",
      alignItems: "center",
      padding: "0 20px",
      gap: "12px",
      fontFamily: "'Caveat', 'Comic Sans MS', cursive",
      fontSize: "18px",
      zIndex: "1000",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    });

    return toolbar;
  }

  /**
   * Add a button to the toolbar
   * @param id Unique identifier for the button
   * @param label Display text for the button
   * @param onClick Click handler callback
   * @returns The created button element
   */
  addButton(id: string, label: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement("button");
    button.id = id;
    button.textContent = label;
    button.className = "hexcrawl-toolbar-button";

    // Hand-drawn button styling
    Object.assign(button.style, {
      fontFamily: "'Caveat', 'Comic Sans MS', cursive",
      fontSize: "20px",
      padding: "8px 16px",
      backgroundColor: "#fffef0",
      border: "2px solid #333",
      borderRadius: "4px",
      cursor: "pointer",
      transition: "all 0.15s ease",
      boxShadow: "2px 2px 0 rgba(0, 0, 0, 0.2)",
      position: "relative",
      fontWeight: "600",
      color: "#333",
      userSelect: "none",
    });

    // Hover effect
    button.addEventListener("mouseenter", () => {
      if (!button.disabled) {
        Object.assign(button.style, {
          backgroundColor: "#f0ead6",
          transform: "translate(-1px, -1px)",
          boxShadow: "3px 3px 0 rgba(0, 0, 0, 0.25)",
        });
      }
    });

    button.addEventListener("mouseleave", () => {
      Object.assign(button.style, {
        backgroundColor: "#fffef0",
        transform: "translate(0, 0)",
        boxShadow: "2px 2px 0 rgba(0, 0, 0, 0.2)",
      });
    });

    // Click effect
    button.addEventListener("mousedown", () => {
      if (!button.disabled) {
        Object.assign(button.style, {
          transform: "translate(1px, 1px)",
          boxShadow: "1px 1px 0 rgba(0, 0, 0, 0.2)",
        });
      }
    });

    button.addEventListener("mouseup", () => {
      if (!button.disabled) {
        Object.assign(button.style, {
          transform: "translate(0, 0)",
          boxShadow: "2px 2px 0 rgba(0, 0, 0, 0.2)",
        });
      }
    });

    // Attach click handler
    button.addEventListener("click", (e) => {
      e.preventDefault();
      if (!button.disabled) {
        onClick();
      }
    });

    this.buttons.set(id, button);
    this.toolbar.appendChild(button);

    return button;
  }

  /**
   * Enable or disable a button
   * @param id Button identifier
   * @param enabled Whether the button should be enabled
   */
  setButtonEnabled(id: string, enabled: boolean): void {
    const button = this.buttons.get(id);
    if (!button) {
      console.warn(`Button with id "${id}" not found`);
      return;
    }

    button.disabled = !enabled;

    if (enabled) {
      Object.assign(button.style, {
        cursor: "pointer",
        opacity: "1",
        color: "#333",
      });
    } else {
      Object.assign(button.style, {
        cursor: "not-allowed",
        opacity: "0.5",
        color: "#999",
        backgroundColor: "#fffef0",
        transform: "translate(0, 0)",
        boxShadow: "2px 2px 0 rgba(0, 0, 0, 0.1)",
      });
    }
  }

  /**
   * Remove a button from the toolbar
   * @param id Button identifier
   */
  removeButton(id: string): void {
    const button = this.buttons.get(id);
    if (button) {
      this.toolbar.removeChild(button);
      this.buttons.delete(id);
    }
  }

  /**
   * Get a button element by id
   * @param id Button identifier
   * @returns The button element or undefined
   */
  getButton(id: string): HTMLButtonElement | undefined {
    return this.buttons.get(id);
  }

  /**
   * Clean up and remove the toolbar from the DOM
   */
  destroy(): void {
    // Remove all event listeners by clearing buttons
    this.buttons.clear();

    // Remove toolbar from container
    if (this.toolbar.parentNode) {
      this.toolbar.parentNode.removeChild(this.toolbar);
    }
  }

  /**
   * Add a separator to the toolbar
   */
  addSeparator(): HTMLElement {
    const separator = document.createElement("div");
    separator.className = "hexcrawl-toolbar-separator";

    Object.assign(separator.style, {
      width: "2px",
      height: "30px",
      backgroundColor: "#333",
      opacity: "0.3",
      margin: "0 8px",
    });

    this.toolbar.appendChild(separator);
    return separator;
  }

  /**
   * Add custom HTML content to the toolbar
   * @param element The element to add
   */
  addElement(element: HTMLElement): void {
    this.toolbar.appendChild(element);
  }

  /**
   * Add a text label to the toolbar
   * @param text The text to display
   * @returns The created label element
   */
  addLabel(text: string): HTMLElement {
    const label = document.createElement("span");
    label.textContent = text;
    label.className = "hexcrawl-toolbar-label";

    Object.assign(label.style, {
      fontFamily: "'Caveat', 'Comic Sans MS', cursive",
      fontSize: "20px",
      color: "#333",
      fontWeight: "500",
      padding: "0 8px",
      userSelect: "none",
    });

    this.toolbar.appendChild(label);
    return label;
  }
}

/**
 * Create a toolbar with initial buttons for the hexcrawl editor
 * @param container The container element to attach the toolbar to
 * @param onExpand Callback for the expand button
 * @returns The created toolbar instance
 */
export function createDefaultToolbar(
  container: HTMLElement,
  onExpand: () => void
): Toolbar {
  const toolbar = new Toolbar(container);

  // Add the main expand button
  toolbar.addButton("expand", "Expand", onExpand);

  return toolbar;
}
